import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import {
  EngagementPaymentTypes,
  EngagementsService,
  PaymentStatus,
  PaymentStatuses,
} from '@techspert-io/engagements';
import {
  PaymentDateForm,
  PaymentRecordType,
  PaymentStatusForm,
} from '@techspert-io/payments';
import { ToastService } from '@techspert-io/user-alerts';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  Observable,
  Subject,
} from 'rxjs';
import {
  debounceTime,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { NotesDialogComponent } from './notes-dialog/notes-dialog.component';
import { IPaymentDashboardEngagement } from './payments-models';

type QueryForm = FormGroup<
  Partial<{
    date: PaymentDateForm;
    status: PaymentStatusForm;
  }>
>;

type PaymentsForm = FormGroup<{
  query: QueryForm;
  filter: FormGroup<{
    search: FormControl<string>;
    paymentTypes: FormControl<EngagementPaymentTypes[]>;
  }>;
}>;

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
  standalone: false,
})
export class PaymentsComponent implements OnInit, OnDestroy {
  private sort: MatSort;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }
  private paginator: MatPaginator;
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  private destroy$ = new Subject();
  private sidebarDimensions = {
    width: '375px',
    height: '100vh',
    position: { right: '0' },
  };

  paymentTypes: EngagementPaymentTypes[] = [];
  displayedColumns = [
    'Select',
    'expertName',
    'email',
    'engagementTitle',
    'owner',
    'amountOwed',
    'currency',
    'paymentType',
    'unitsUsed',
    'dateOfEngagement',
    'paymentStatus',
    'Edit',
  ];
  dataSource: MatTableDataSource<IPaymentDashboardEngagement>;
  engagements: IPaymentDashboardEngagement[] = [];
  engagementsSelection = new SelectionModel<IPaymentDashboardEngagement>(
    true,
    []
  );
  showLoader = true;

  getEngagements$ = new Observable<IPaymentDashboardEngagement[]>();

  disablePaymentActions = true;

  form: PaymentsForm = new FormGroup({
    query: new FormGroup({}),
    filter: new FormGroup({
      search: new FormControl<string>(''),
      paymentTypes: new FormControl<EngagementPaymentTypes[]>([]),
    }),
  });

  private statusUpdates$ = new BehaviorSubject<Record<string, PaymentStatus>>(
    {}
  );

  get PaymentRecordType() {
    return PaymentRecordType;
  }

  get paymentStatuses() {
    return PaymentStatuses;
  }

  get queryForm() {
    return this.form.controls.query;
  }

  get filterForm() {
    return this.form.controls.filter;
  }

  constructor(
    private dialog: MatDialog,
    private engagementsService: EngagementsService,
    private route: ActivatedRoute,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    const opportunityId$ = this.route.queryParamMap.pipe(
      map((p) => p.get('opportunityId')),
      tap((opportunityId) => (this.disablePaymentActions = !!opportunityId))
    );

    this.showLoader = true;

    const queryChanges$ = combineLatest([
      this.queryForm.valueChanges.pipe(startWith(this.queryForm.value)),
      opportunityId$,
    ]).pipe(
      debounceTime(300),
      switchMap(([{ date, status }, opportunityId]) => {
        if (!date || !status || this.queryForm.invalid) {
          this.showLoader = false;
          return EMPTY;
        }

        this.showLoader = true;

        return this.engagementsService
          .queryExtended({
            paymentStatuses: status.paymentStatuses,
            dateMin: moment(date.from),
            dateMax: moment(date.to),
            opportunityId,
            verificationStatus: status.verificationStatus,
          })
          .pipe(finalize(() => (this.showLoader = false)));
      })
    );

    const filterChanges$ = this.filterForm.valueChanges.pipe(
      startWith(this.filterForm.value)
    );

    combineLatest([queryChanges$, filterChanges$])
      .pipe(
        takeUntil(this.destroy$),
        tap(([engagements]) => {
          this.engagements = engagements;

          this.setPaymentTypeOptions();
          this.setTableData();

          this.engagementsSelection.clear();
        })
      )
      .subscribe();

    this.statusUpdates$
      .pipe(
        tap((statusUpdates) => {
          this.engagements = this.engagements.map((engagement) => {
            const paymentStatus = statusUpdates[engagement.engagementId];

            return paymentStatus
              ? {
                  ...engagement,
                  paymentStatus,
                }
              : engagement;
          });

          this.setTableData();
          this.engagementsSelection.clear();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  isAllSelected() {
    if (!this.dataSource || !this.dataSource.data) {
      return true;
    }

    const numSelected = this.engagementsSelection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.engagementsSelection.clear()
      : this.dataSource.data.forEach((engagement) =>
          this.engagementsSelection.select(engagement)
        );
  }

  checkboxLabel(row?: IPaymentDashboardEngagement) {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all engagements`;
    }
    return `${
      this.engagementsSelection.isSelected(row) ? 'deselect' : 'select'
    } engagement: ${row.engagementTitle}`;
  }

  openNotesDialog(engagement: IPaymentDashboardEngagement) {
    this.dialog
      .open(NotesDialogComponent, {
        ...this.sidebarDimensions,
        data: {
          engagement,
          disableTransactionId: this.disablePaymentActions,
        },
      })
      .afterClosed()
      .subscribe();
  }

  updateEngagementPaymentStatus(
    engagementId: string,
    paymentStatus: PaymentStatus
  ) {
    if (this.disablePaymentActions) {
      this.toastService.sendMessage(
        'Updating payment status is not permitted',
        'danger'
      );

      return;
    }

    this.engagementsService
      .updateLegacy({
        engagementId,
        paymentStatus,
      })
      .subscribe();
  }

  receiveStatusUpdates(statusUpdates: Record<string, PaymentStatus>) {
    this.statusUpdates$.next(statusUpdates);
  }

  private setPaymentTypeOptions() {
    this.paymentTypes = [
      ...new Set(this.engagements.map(({ paymentType }) => paymentType)),
    ].sort();

    if (!this.filterForm.controls.paymentTypes.dirty) {
      this.filterForm.controls.paymentTypes.setValue(
        this.paymentTypes.filter((d) => d !== 'thirdParty'),
        { emitEvent: false }
      );
    }
  }

  private setTableData() {
    const searchTerm = this.filterForm.value.search || '';
    const paymentTypes = this.filterForm.value.paymentTypes || [];

    const filteredEngagements = this.engagements.filter(
      (engagement) =>
        (engagement.expertName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
          (searchTerm.length > 8 &&
            engagement.expertId &&
            engagement.expertId
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))) &&
        paymentTypes.includes(engagement.paymentType)
    );

    this.dataSource = new MatTableDataSource<IPaymentDashboardEngagement>(
      filteredEngagements
    );

    this.setDataSourceAttributes();
  }

  private setDataSourceAttributes() {
    if (!this.dataSource) {
      return;
    }

    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }
}
